import React, { useState, useEffect, useRef } from 'react';
import { GET, POST, PUT } from '../services/api';
import TenureSelect from '../components/TenureSelect'; 
import { useMasterChapter, MasterChapterLockNote } from '../context/MasterChapter';

// --- CONFIGURATION: Updated Mapping based on new JSON ---
const COMMITTEE_CONFIG = [
  // Core Leadership (Single Users)
  { label: 'President', apiKey: 'president', isArray: false },
  { label: 'Vice President', apiKey: 'vicePresident', isArray: false },
  { label: 'Secretary', apiKey: 'secretary', isArray: true },

  // Committees & Coordinators (Arrays/Multiple Users)
  { label: 'Membership Committee', apiKey: 'membershipCommity', isArray: true },
  { label: 'Chapter Supports', apiKey: 'chapterSupports', isArray: true },
  { label: 'Chapter Growth', apiKey: 'chaptersGrowth', isArray: true },
  { label: 'Specific Ask', apiKey: 'specificAsk', isArray: true },
  { label: 'Event Coordinator', apiKey: 'eventCoordinator', isArray: true },
  { label: 'Presentation Coordinator', apiKey: 'presentationCoordinator', isArray: true },
  { label: 'Mentorship Coordinator', apiKey: 'mentorshipCoordinator', isArray: true },
  { label: 'Education Coordinator', apiKey: 'educationCoordinator', isArray: true },
  { label: 'IT Coordinator', apiKey: 'itCoordinator', isArray: true },
  { label: 'Attendance Coordinator', apiKey: 'attendanceCoordinator', isArray: true },
  { label: 'Power Team', apiKey: 'powerTeam', isArray: true }
];

// --- ICONS ---
const Icons = {
  Edit: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>,
  Trash: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
  X: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>,
  Plus: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>,
  ChevronRight: () => <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>,
  ArrowLeft: () => <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>,
  Building: () => <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
  Save: () => <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
};


const CommitteeManager = () => {
  // --- GLOBAL STATE ---
  const [viewMode, setViewMode] = useState('LIST'); // 'LIST' | 'VIEW' | 'ADD' | 'EDIT'
  const [cityData, setCityData] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [memberData, setMemberData] = useState(null); 
  const [loading, setLoading] = useState(false);
  const [adminProfile, setAdminProfile] = useState(null);
  const [isSubAdmin, setIsSubAdmin] = useState(false);
  const master = useMasterChapter();
  const openedMasterRef = useRef('');


  // --- INITIAL DATA FETCH ---
  useEffect(() => {
    fetchAdminProfile();
  }, []);

  // Fetch admin profile to check permissions
  const fetchAdminProfile = async () => {
    try {
      const response = await GET('/admin/profile'); // Assume this endpoint exists
      if (response.success) {
        setAdminProfile(response.data);
        setIsSubAdmin(response.data.role !== 'super-admin');
        if (response.data.role !== 'super-admin' && response.data.chaptersId?.length > 0) {
          // Auto-select first assigned chapter for sub-admin
          const firstChapterId = response.data.chaptersId[0];
          const citiesResp = await GET('/committees/'); // Get city structure
          if (citiesResp.success) {
            // Find chapter in city data & select
            const findAndSelectChapter = (cities) => {
              for (let city of cities) {
                if (city.chapters) {
                  const chapter = city.chapters.find(ch => (ch._id || ch.chapterId) === firstChapterId);
                  if (chapter) {
                    handleChapterSelect(chapter);
                    return;
                  }
                }
              }
            };
            setCityData(citiesResp.data);
            findAndSelectChapter(citiesResp.data);
          }
        } else {
          fetchCities();
        }
      }
    } catch (error) {
      console.error('Failed to fetch admin profile:', error);
      fetchCities(); // Fallback
    }
  };


  const fetchCities = async () => {
    try {
      setLoading(true);
      const response = await GET('/committees/');
      if (response.success) {
        setCityData(response.data);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    } finally {
      setLoading(false);
    }
  };

  // --- ACTIONS ---

  const handleChapterSelect = async (chapter) => {
    const cId = chapter.chapterId || chapter._id || chapter.id;
    // For sub-admins, double-check permission (backend already validates)
    if (isSubAdmin && !adminProfile.chaptersId.some(id => id === cId)) {
      alert('Access denied: You can only view your assigned chapters.');
      return;
    }
    console.log("Selected chapter:", chapter, "chapterId:", cId);
    setSelectedChapter({ ...chapter, id: cId });

    try {
      setLoading(true);
      console.log("Fetching committee data for chapter:", cId);
      const response = await GET(`/committees/chapter/${cId}`);
      console.log("Committee API response:", response);
      if (response.success && response.data && response.data.length > 0) {
        console.log("Setting memberData:", response.data[0]);
        setMemberData(response.data[0]);
        setViewMode('VIEW');
      } else {
        console.log("No committee data found, setting empty object");
        setMemberData({});
        setViewMode('VIEW');
      }
    } catch (error) {
      console.error("Error details:", error);
      alert("Failed to load committee data. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  // Master chapter filter (super-admin header): open that chapter's committee directly;
  // clearing it goes back to the full city/chapter list.
  useEffect(() => {
    if (openedMasterRef.current === master.chapterId) return;
    if (!master.chapterId) {
      openedMasterRef.current = '';
      setViewMode('LIST');
      setSelectedChapter(null);
      setMemberData(null);
      return;
    }
    if (!cityData.length) return; // wait for the list so the chapter object has its name
    openedMasterRef.current = master.chapterId;
    const found = cityData
      .flatMap(city => city.chapters || [])
      .find(ch => (ch.chapterId || ch._id || ch.id) === master.chapterId);
    handleChapterSelect(found || { chapterId: master.chapterId, chapterName: master.chapterName });
  }, [master.chapterId, cityData]); // eslint-disable-line react-hooks/exhaustive-deps

  // While the master filter is set, the list only offers that chapter
  const visibleCities = master.chapterId
    ? cityData
        .map(city => ({ ...city, chapters: (city.chapters || []).filter(ch => (ch.chapterId || ch._id || ch.id) === master.chapterId) }))
        .filter(city => city.chapters.length > 0)
    : cityData;

  const handleAddNew = () => {
    setSelectedChapter(null);
    setMemberData({});
    setViewMode('ADD');
  };

  const handleEdit = () => {
    setViewMode('EDIT');
  };

  const handleBack = () => {
    if (viewMode === 'EDIT') {
      setViewMode('VIEW');
    } else {
      setViewMode('LIST');
      setSelectedChapter(null);
      setMemberData(null);
    }
  };

  const handleSaveData = async (updatedCommitteeData, selectedChapterInfo, formCity, formChapter, formTenureId) => {
    try {
      setLoading(true);
      console.log("handleSaveData called with mode:", viewMode);
      console.log("updatedCommitteeData:", updatedCommitteeData);
      console.log("formCity:", formCity, "formChapter:", formChapter);

      if (!formCity || !formChapter) {
        alert("Please select City and Chapter");
        return;
      }

      // Transform formData to API format
      const apiData = {
        cityId: formCity,
        chapterId: formChapter,
        // '' means unassigned; the backend stores null and treats it as the
        // chapter's legacy committee (docs/tenure-feature.md section 8).
        tenureId: formTenureId || null
      };

      // Map form labels back to API keys - respect backend schema (single vs array)
      COMMITTEE_CONFIG.forEach(config => {
        const members = updatedCommitteeData[config.label] || [];
        const memberIds = members
          .map(m => m.user_id) // user_id is the actual user ID from search API
          .filter(id => id != null && id !== undefined && id !== '');

        if (config.isArray) {
          // Array roles: send array of user IDs
          apiData[config.apiKey] = memberIds;
        } else {
          // Single roles: send first user ID or null
          apiData[config.apiKey] = memberIds.length > 0 ? memberIds[0] : null;
        }
      });

      console.log("Final apiData to send:", apiData);

      let response;
      if (viewMode === 'ADD') {
        response = await POST('/committees/', apiData);
      } else {
        // For edit, we need the committee ID
        const committeeId = memberData._id || memberData.id;
        console.log("Editing committee with ID:", committeeId);
        response = await PUT(`/committees/${committeeId}`, apiData);
      }

      console.log("API response:", response);

      if (response.success) {
        alert(`Committee ${viewMode === 'ADD' ? 'created' : 'updated'} successfully!`);
        // Refresh and go to appropriate view
        fetchCities();
        if (viewMode === 'ADD') {
          setViewMode('LIST');
        } else {
          // Refresh the current chapter data using the formChapter (which may have been updated)
          const cId = formChapter;
          console.log("Refreshing committee data after save for chapter:", cId);
          const chapterResponse = await GET(`/committees/chapter/${cId}`);
          console.log("Refresh response:", chapterResponse);
          if (chapterResponse.success && chapterResponse.data && chapterResponse.data.length > 0) {
            console.log("Setting refreshed memberData:", chapterResponse.data[0]);
            setMemberData(chapterResponse.data[0]);
          } else {
            console.log("No data in refresh response");
            setMemberData({});
          }
          setViewMode('VIEW');
        }
      } else {
        alert(`Failed to ${viewMode === 'ADD' ? 'create' : 'update'} committee: ${response.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error saving committee:", error);
      alert(`Error ${viewMode === 'ADD' ? 'creating' : 'updating'} committee: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      
      {loading && (
        <div className="fixed inset-0 bg-white/50 z-50 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* --- VIEW 1: CITY LIST (HOME) --- */}
      {viewMode === 'LIST' && (
        <div className="animate-fade-in-up">
           <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Committees</h2>
              <button 
                onClick={handleAddNew}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-sm transition-colors"
              >
                <Icons.Plus /> Add New Committee
              </button>
           </div>

           {master.chapterId && <div className="mb-4"><MasterChapterLockNote /></div>}
           <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {visibleCities.length > 0 ? visibleCities.map((city, index) => (
              <div key={city.cityId || index} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="bg-blue-50/50 p-4 border-b border-gray-100 flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm"><Icons.Building /></div>
                  <h3 className="text-lg font-bold text-gray-800">{city.cityName}</h3>
                  <span className="ml-auto text-xs font-semibold bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                    {city.chapters ? city.chapters.length : 0} Chapters
                  </span>
                </div>
                <div className="divide-y divide-gray-100">
                  {city.chapters && city.chapters.map((chapter, cIndex) => (
                    <button
                      key={chapter.chapterId || cIndex}
                      onClick={() => handleChapterSelect(chapter)}
                      className="w-full text-left p-4 hover:bg-gray-50 transition-colors flex items-center justify-between group"
                    >
                      <span className="font-medium text-gray-700 group-hover:text-blue-600">
                          {chapter.chapterName || chapter.name}
                      </span>
                      <Icons.ChevronRight />
                    </button>
                  ))}
                </div>
              </div>
            )) : ( !loading && <div className="col-span-3 text-center text-gray-500">No cities found.</div> )}
          </div>
        </div>
      )}

      

      {/* --- VIEW 2: READ ONLY DETAILS --- */}
      {viewMode === 'VIEW' && selectedChapter && (
        <ViewCommitteeDetails 
            chapter={selectedChapter} 
            data={memberData} 
            onBack={handleBack} 
            onEdit={handleEdit}
        />
      )}

      {/* --- VIEW 3 & 4: ADD / EDIT FORM --- */}
      {(viewMode === 'ADD' || viewMode === 'EDIT') && (
        <ManageCommitteeForm 
            mode={viewMode}
            initialData={viewMode === 'EDIT' ? memberData : {}}
            selectedChapter={selectedChapter}
            allCities={cityData}
            onBack={handleBack}
            onSave={handleSaveData}
        />
      )}

    </div>
  );
};

// ==============================================================================
// SUB-COMPONENT: MANAGE FORM (Handles both Add & Edit)
// ==============================================================================
const ManageCommitteeForm = ({ mode, initialData, selectedChapter, allCities, onBack, onSave }) => {
    // State for Form Data
    const [formData, setFormData] = useState({});

    // State for Dropdown Selections
    const [formCity, setFormCity] = useState('');
    const [formChapter, setFormChapter] = useState('');
    const [formTenureId, setFormTenureId] = useState('');
    const [availableChapters, setAvailableChapters] = useState([]);
    const [allAvailableCities, setAllAvailableCities] = useState([]);

    const master = useMasterChapter();

    // UI States
    const [editingType, setEditingType] = useState(null); 
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchWrapperRef = useRef(null);

    // Fetch all cities for the form
    useEffect(() => {
        const fetchAllCities = async () => {
            try {
                console.log("Fetching cities from /committee/cities");
                const response = await GET('/committee/cities');
                console.log("Cities API response:", response);
                if (response.success) {
                    console.log("Setting cities data:", response.data);
                    setAllAvailableCities(response.data);
                } else {
                    console.log("API response not successful:", response);
                }
            } catch (error) {
                console.error("Error fetching all cities:", error);
            }
        };
        fetchAllCities();
    }, []);

    // Initialize Form Data & Dropdowns
    useEffect(() => {
        console.log("ManageCommitteeForm useEffect triggered");
        console.log("Mode:", mode);
        console.log("InitialData:", initialData);

        // 1. Prepare Committee Data
        const loadedData = {};
        COMMITTEE_CONFIG.forEach(config => {
            let members = [];
            if (config.isCombined) {
                config.apiKey.forEach(key => {
                    if (initialData[key]) members.push(initialData[key]);
                });
            } else if (config.isArray) {
                const data = initialData[config.apiKey];
                if (Array.isArray(data)) {
                    members = data;
                } else if (data) {
                    members = [data];
                } else {
                    members = [];
                }
            } else {
                if (initialData[config.apiKey]) members.push(initialData[config.apiKey]);
            }
            loadedData[config.label] = members;
        });

        console.log("Loaded data for form:", loadedData);

        if (mode === 'ADD') {
            COMMITTEE_CONFIG.forEach(c => loadedData[c.label] = []);
            // Reset Dropdowns for Add Mode (pre-filled from the master chapter filter, still editable)
            setFormCity(master.cityId);
            setFormChapter(master.chapterId);
            setAvailableChapters([]);
            if (master.cityId) {
                GET(`/committee/chapters/city/${master.cityId}`)
                    .then(response => { if (response.success) setAvailableChapters(response.data); })
                    .catch(error => console.error("Error fetching chapters for master city:", error));
            }
        } else if (mode === 'EDIT' && initialData && initialData.cityId && initialData.chapterId) {
            // Pre-fill Dropdowns for Edit Mode using committee data
            const committeeCityId = initialData.cityId;
            const committeeChapterId = initialData.chapterId;

            console.log("Setting edit mode cityId:", committeeCityId, "chapterId:", committeeChapterId);

            setFormCity(committeeCityId);
            setFormTenureId(initialData.tenureId || '');
            setFormChapter(committeeChapterId);

            // Fetch chapters for this city
            const fetchChaptersForCity = async () => {
                try {
                    const response = await GET(`/committee/chapters/city/${committeeCityId}`);
                    if (response.success) {
                        setAvailableChapters(response.data);
                    }
                } catch (error) {
                    console.error("Error fetching chapters for edit:", error);
                }
            };
            fetchChaptersForCity();
        }

        setFormData(loadedData);
    }, [initialData, mode]); // eslint-disable-line react-hooks/exhaustive-deps -- master only pre-fills when the form opens

    // Handle City Selection
    const handleCityChange = async (e) => {
        const cityId = e.target.value;
        setFormCity(cityId);
        setFormTenureId('');
        setFormChapter(''); // Reset chapter when city changes

        if (cityId) {
            try {
                const response = await GET(`/committee/chapters/city/${cityId}`);
                if (response.success) {
                    setAvailableChapters(response.data);
                } else {
                    setAvailableChapters([]);
                }
            } catch (error) {
                console.error("Error fetching chapters:", error);
                setAvailableChapters([]);
            }
        } else {
            setAvailableChapters([]);
        }
    };

    // --- Search Logic ---
    const handleSearchChange = async (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        console.log("Search query:", query);
        if (query.length > 0) {
            try {
                const params = new URLSearchParams({
                    search: query
                });

                // ALWAYS include chapterId for sub-admins (required by backend now)
                let chapterId = formChapter || (mode === 'EDIT' && initialData?.chapterId);
                if (!chapterId && window.adminProfile?.chaptersId?.[0]) { // Fallback to first assigned
                  chapterId = window.adminProfile.chaptersId[0];
                }
                if (chapterId) {
                    params.append('chapterId', chapterId);
                }

                // City optional
                const cityId = formCity || (mode === 'EDIT' && initialData?.cityId);
                if (cityId) {
                    params.append('cityId', cityId);
                }

                console.log("Searching with cityId:", cityId, "chapterId:", chapterId);

                const response = await GET(`/committees/search?${params}`);
                console.log("Search response:", response);
                if (response.success) {
                    setFilteredUsers(response.data);
                    setShowSuggestions(true);
                } else {
                    setFilteredUsers([]);
                    setShowSuggestions(false);
                }
            } catch (error) {
                console.error("Error searching users:", error);
                setFilteredUsers([]);
                setShowSuggestions(false);
            }
        } else {
            setShowSuggestions(false);
        }
    };


    const selectUser = (user) => {
        if (editingType) {
            const currentList = formData[editingType] || [];
            const userId = user.user_id;
            const config = COMMITTEE_CONFIG.find(c => c.label === editingType);

            if (config && !config.isArray) {
                // Single role: replace the current member and close search
                setFormData(prevData => ({
                    ...prevData,
                    [editingType]: [user]
                }));
                setSearchQuery('');
                setShowSuggestions(false);
                setEditingType(null); // Close the search for single roles
            } else {
                // Array role: add if not already present, keep search open for more selections
                if (!currentList.find(u => u.user_id === userId)) {
                    setFormData(prevData => ({
                        ...prevData,
                        [editingType]: [...currentList, user]
                    }));
                    setSearchQuery(''); // Clear search query so user can search for next member
                }
            }
        }
    };

    const removeUser = (type, userId) => {
        setFormData({
            ...formData,
            [type]: formData[type].filter(u => (u.user_id?._id || u.user_id || u.id || u._id) !== userId)
        });
    };

    const handleSaveClick = () => {
        if (!formCity || !formChapter) {
            alert("Please select City and Chapter");
            return;
        }

        const chapterInfo = availableChapters.find(c => (c._id || c.id).toString() === formChapter.toString());
        onSave(formData, chapterInfo, formCity, formChapter, formTenureId);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="animate-fade-in-up">
            <button 
                onClick={onBack}
                className="mb-4 flex items-center text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
            >
                <Icons.ArrowLeft /> 
                {mode === 'ADD' ? 'Cancel Adding' : `Back to View`}
            </button>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                
                {/* HEADER AREA */}
                <div className="p-5 border-b border-gray-100 bg-gray-50 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">
                            {mode === 'ADD' ? 'Add New Committee' : 'Edit Committee'}
                        </h2>
                        <p className="text-xs text-gray-500 mt-1">
                            {mode === 'ADD' ? 'Select location and assign members.' : 'Update location or manage members.'}
                        </p>
                    </div>

                    <button 
                        onClick={handleSaveClick}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center text-sm font-medium shadow-sm"
                    >
                        <Icons.Save /> Save Committee
                    </button>
                </div>

                {/* LOCATION DROPDOWNS (VISIBLE IN BOTH ADD & EDIT) */}
                <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-gray-100 bg-blue-50/20">
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">City</label>
                        <select 
                            className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none"
                            value={formCity}
                            onChange={handleCityChange}
                        >
                            <option value="">-- Select City --</option>
                            {allAvailableCities.map(city => (
                                <option key={city._id || city.id} value={city._id || city.id}>{city.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Chapter</label>
                        <select 
                            className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none"
                            value={formChapter}
                            onChange={(e) => setFormChapter(e.target.value)}
                            disabled={!formCity}
                        >
                            <option value="">-- Select Chapter --</option>
                            {availableChapters.map(chap => (
                                <option key={chap._id || chap.id} value={chap._id || chap.id}>
                                    {chap.chapterName || chap.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <TenureSelect
                            chapterId={formChapter}
                            value={formTenureId}
                            onChange={setFormTenureId}
                            label="Tenure (term served)"
                            labelClassName="block text-xs font-semibold text-gray-700 mb-1"
                        />
                    </div>
                </div>

                {/* EDITING TABLE */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/50">
                                <th className="p-4 py-3 font-semibold text-gray-700 w-1/3 border-r border-gray-100">Committee Role</th>
                                <th className="p-4 py-3 font-semibold text-gray-700">Assigned Members</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {COMMITTEE_CONFIG.map((config, index) => {
                                const members = formData[config.label] || [];
                                const isEditing = editingType === config.label;

                                return (
                                    <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="p-4 text-sm font-medium text-gray-800 align-top border-r border-gray-100">
                                            {config.label}
                                        </td>
                                        <td className="p-4 align-top">
                                            <div className="flex flex-col gap-2">
                                                
                                                {/* List Existing Members */}
                                                {members.map((member, mIdx) => (
                                                    <div key={mIdx} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200 shadow-sm max-w-md">
                                                        <div className="flex items-center gap-3">
                                                            <img 
                                                                src={member.profile_picture || "https://placehold.co/150"} 
                                                                alt="" 
                                                                className="w-8 h-8 rounded-full object-cover" 
                                                            />
                                                            <span className="text-sm text-gray-700">{member.full_name || member.name}</span>
                                                        </div>
                                                        <button
                                                            onClick={() => removeUser(config.label, member.user_id?._id || member.user_id || member.id || member._id)}
                                                            className="text-red-400 hover:text-red-600 p-1"
                                                        >
                                                            <Icons.X />
                                                        </button>
                                                    </div>
                                                ))}

                                                {/* Inline Add Logic */}
                                                {!isEditing ? (
                                                     <button
                                                        onClick={() => {
                                                            console.log("Add Member clicked for:", config.label);
                                                            setEditingType(config.label);
                                                            setSearchQuery('');
                                                        }}
                                                        className="flex items-center gap-2 text-xs font-semibold text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-md w-fit mt-1 border border-dashed border-blue-200"
                                                    >
                                                        <Icons.Plus /> Add Member
                                                    </button>
                                                ) : (
                                                    <div className="mt-2 flex items-center gap-2" ref={searchWrapperRef}>
                                                        <div className="relative w-full max-w-xs">
                                                            <input
                                                                type="text"
                                                                autoFocus
                                                                value={searchQuery}
                                                                onChange={(e) => {
                                                                    console.log("Search input changed:", e.target.value);
                                                                    handleSearchChange(e);
                                                                }}
                                                                placeholder="Search user..."
                                                                className="w-full border border-blue-400 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                                                            />
                                                            {showSuggestions && (
                                                                <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                                                                    {filteredUsers.length > 0 ? filteredUsers.map(user => (
                                                                        <div
                                                                            key={user.user_id}
                                                                            onClick={() => {
                                                                                console.log("User selected:", user);
                                                                                selectUser(user);
                                                                            }}
                                                                            className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer"
                                                                        >
                                                                            <img src={user.profile_picture} alt="" className="w-6 h-6 rounded-full mr-2 object-cover" />
                                                                            <span className="text-sm text-gray-700">{user.full_name}</span>
                                                                        </div>
                                                                    )) : (
                                                                        <div className="p-2 text-xs text-gray-500">No users found</div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={() => {
                                                                console.log("Done/Cancel clicked");
                                                                setEditingType(null);
                                                            }}
                                                            className="text-gray-500 hover:text-gray-700 p-2"
                                                        >
                                                            {config && config.isArray ? 'Done' : <Icons.X />}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// ==============================================================================
// SUB-COMPONENT: VIEW DETAILS (Read Only)
// ==============================================================================
const ViewCommitteeDetails = ({ chapter, data, onBack, onEdit }) => {
    return (
        <div className="animate-fade-in-up">
            <div className="flex justify-between items-center mb-4">
                <button 
                    onClick={onBack}
                    className="flex items-center text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
                >
                    <Icons.ArrowLeft />
                    Back to Cities ({chapter.chapterName || chapter.name})
                </button>
                <button 
                    onClick={onEdit}
                    className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-blue-300 hover:text-blue-600 transition-all shadow-sm"
                >
                    <Icons.Edit /> Edit Committee
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/50">
                        <th className="p-4 py-3 font-semibold text-gray-700 w-1/4 border-r border-gray-100">Committee Type</th>
                        <th className="p-4 py-3 font-semibold text-gray-700">Member</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                     
                    {COMMITTEE_CONFIG.map((config, index) => {
                        let members = [];
                        if (data) {
                            if (config.isCombined) {
                                config.apiKey.forEach(key => {
                                    if(data[key]) members.push(data[key]);
                                });
                            } else if (config.isArray) {
                                const memberData = data[config.apiKey];
                                if (Array.isArray(memberData)) {
                                    members = memberData;
                                } else if (memberData) {
                                    members = [memberData];
                                } else {
                                    members = [];
                                }
                            } else {
                                if (data[config.apiKey]) members.push(data[config.apiKey]);
                            }
                        }

                        return (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                                <td className="p-4 py-4 text-sm font-medium text-gray-800 align-middle border-r border-gray-100">
                                    {config.label}
                                </td>
                                <td className="p-4 py-4 align-middle">
                                    {members && members.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {members.map((member, mIndex) => (
                                                <div key={member.user_id || member._id || mIndex} className="flex items-center space-x-3 bg-gray-50 p-2 rounded border">
                                                <img
                                                    src={member.profile_picture || "https://placehold.co/150"}
                                                    alt={member.full_name}
                                                    className="w-8 h-8 rounded-full object-cover border border-gray-200"
                                                    onError={(e) => {e.target.src = "https://placehold.co/150"}}
                                                />
                                                <span className="text-sm font-medium text-gray-700">
                                                    {member.full_name}
                                                </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-gray-400 text-sm italic">-</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
                </div>
            </div>
             
            {data?.updatedby?.first_name && (
                <div className="mt-4 text-right text-xs text-gray-400">
                    Last updated by: {data.updatedby.first_name} {data.updatedby.last_name} 
                    {' '}on {new Date(data.updatedby.actionAt).toLocaleDateString()}
                </div>
            )}
        </div>
    );
};

export default CommitteeManager;